import { Link } from "react-router-dom";
import { usePppsByKr } from "@/hooks/usePpps";
import { Ppp } from "@/types";
import { UserAvatar } from "@/components/UserAvatar";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { Layers } from "lucide-react";
import { PPP_TYPE_BADGE, PPP_TYPE_DOT } from "@/components/ppp/ppp-constants";
import { PppLock } from "@/components/ppp/PppLock";
import { CATEGORY_BADGE, CATEGORY_LABEL } from "./okrConstants";
import { TREE_ROW_BASE, TREE_ROW_COLS } from "./okr-tree-constants";

/** Third tree level: PPPs rolled up under a KR (no further nesting — deliverables live on /priorities). */
const INDENT = "w-[62px] flex-shrink-0";

function PppRow({ ppp }: { ppp: Ppp }) {
    return (
        <div className={cn(TREE_ROW_BASE, "hover:bg-muted/10")} style={TREE_ROW_COLS}>
            <div className={cn(
                "absolute left-0 inset-y-1.5 w-[3px] rounded-r opacity-0 group-hover:opacity-100 transition-opacity",
                PPP_TYPE_DOT[ppp.type],
            )} />

            {/* Name cell */}
            <div className="flex min-w-0 items-center gap-0">
                <div className={INDENT} />
                <span className={cn(
                    "mr-2 inline-flex flex-shrink-0 items-center rounded border px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide whitespace-nowrap",
                    PPP_TYPE_BADGE[ppp.type],
                )}>
                    {ppp.type}
                </span>
                <span className="flex-1 truncate text-[12.5px] text-muted-foreground">{ppp.name}</span>
                {ppp.isConfidential && <PppLock />}
            </div>

            {/* Status column → category lane */}
            <div>
                {ppp.category && (
                    <span className={cn(
                        "inline-flex items-center rounded border px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide",
                        CATEGORY_BADGE[ppp.category],
                    )}>
                        {CATEGORY_LABEL[ppp.category]}
                    </span>
                )}
            </div>

            {/* Progress column → deliverable count */}
            <div className="flex items-center justify-center">
                <Link
                    to={`/priorities?pppPids=${ppp.pid}`}
                    onClick={(e) => e.stopPropagation()}
                    className="text-[11px] text-muted-foreground hover:text-foreground hover:underline"
                >
                    {ppp.deliverableCount} deliverable{ppp.deliverableCount === 1 ? "" : "s"}
                </Link>
            </div>

            {/* DRI */}
            <div className="flex items-center gap-1.5">
                {ppp.dri ? (
                    <>
                        <UserAvatar src={ppp.dri.avatarUrl} name={ppp.dri.fullName} className="h-[22px] w-[22px] flex-shrink-0" fallbackClassName="text-[9px]" />
                        <span className="truncate text-[11px] text-muted-foreground">{ppp.dri.fullName}</span>
                    </>
                ) : (
                    <span className="text-[11px] text-muted-foreground">—</span>
                )}
            </div>

            <div />
        </div>
    );
}

export function KrPppList({ keyResultPid }: { keyResultPid: string }) {
    const { data, isLoading } = usePppsByKr(keyResultPid);
    const ppps = data?.data ?? [];

    if (isLoading) {
        return (
            <div>
                {Array.from({ length: 2 }).map((_, i) => (
                    <div key={i} className="grid gap-3 px-6 min-h-[52px] items-center border-b border-border/40" style={TREE_ROW_COLS}>
                        <div className="flex items-center gap-2 pl-[62px]"><Skeleton className="h-3 w-48" /></div>
                        <Skeleton className="h-5 w-16 rounded-full" />
                        <Skeleton className="h-3 w-20 mx-auto" />
                        <Skeleton className="h-4 w-20" />
                    </div>
                ))}
            </div>
        );
    }

    if (ppps.length === 0) {
        return (
            <div className="flex items-center gap-2 border-b border-border/40 px-6 pl-[86px] min-h-[36px] text-[12px] text-muted-foreground">
                <Layers className="h-3.5 w-3.5 flex-shrink-0" />
                No PPP linked
            </div>
        );
    }

    return <div>{ppps.map((p) => <PppRow key={p.pid} ppp={p} />)}</div>;
}
